goog.declareModuleId('yext.ui.components.calloutbanner.CalloutBanner');

import {getPageOffsetLeft} from 'goog:goog.style';

import {classNames} from '/ui/lib/classnames';
import {TopLevelContainerContext} from '/ui/lib/containers';
import {yext} from '/ui/lib/msg';
import {useUid} from '/ui/lib/uid';

import * as styles from '/ui/components/calloutbanner/calloutbanner.module.scss';
import ErrorSvg from '/ui/components/calloutbanner/error.svg';
import InformationSvg from '/ui/components/calloutbanner/information.svg';
import WarningSvg from '/ui/components/calloutbanner/warning.svg';
import * as a11yStyles from '/ui/utilities/a11y.module.scss';

/**
 * @enum {symbol}
 */
export const CalloutBannerType = {
  NEUTRAL: Symbol(),
  WARNING: Symbol(),
  ERROR: Symbol(),
};

/**
 * @type {Object<CalloutBannerType, {
 *   containerStyle: string,
 *   icon: React.ReactNode,
 *   srText: string,
 * }>}
 */
const TYPE_TO_STYLE = {
  [CalloutBannerType.NEUTRAL]: {
    containerStyle: styles.containerNeutral,
    icon: <InformationSvg/>,
    srText: yext.msg('Note:'),
  },
  [CalloutBannerType.WARNING]: {
    containerStyle: styles.containerWarning,
    icon: <WarningSvg/>,
    srText: yext.msg('Warning:'),
  },
  [CalloutBannerType.ERROR]: {
    containerStyle: styles.containerError,
    icon: <ErrorSvg/>,
    srText: yext.msg('Error:'),
  },
};

/**
 * @typedef {{
 *   type: !CalloutBannerType,
 *   title?: string,
 *   children?: React.ReactNode,
 * }}
 */
export let CalloutBannerProps;

/**
 * @param {CalloutBannerProps} props
 * @returns {React.ReactElement}
 */
export function CalloutBanner({type, title, children}) {
  const {containerStyle, icon, srText} = TYPE_TO_STYLE[type];
  const uid = useUid();
  const iconId = !title ? uid : undefined;
  const titleId = title ? uid : undefined;

  /** @type {!React.MutableRefObject<?HTMLDivElement>} */
  const containerRef = React.useRef(null);
  const [isFlush, setFlush] = React.useState(false);
  const topLevelContainerRef = React.useContext(TopLevelContainerContext);
  // We care more about the ref changing (i.e. different container component)
  // than the ref element changing.
  React.useEffect(() => {
    const tlcr = topLevelContainerRef;
    setFlush(!!(
      containerRef.current
      && tlcr?.current
      && areHorizontallyFlush(tlcr.current, containerRef.current)
      && widthsMatch(tlcr.current, containerRef.current)
    ));
  }, [topLevelContainerRef]);

  return (
    <div
      role="note"
      aria-labelledby={uid}
      className={classNames(
        containerStyle,
        {[styles.flush]: isFlush},
      )}
      ref={containerRef}
    >
      <div id={iconId} className={styles.icon}>
        {icon}
        <span className={a11yStyles.srOnly}>{srText}</span>
      </div>
      <div className={styles.content}>
        {title && <div id={titleId} className={styles.title}>{title}</div>}
        {children}
      </div>
    </div>
  );
}

/**
 * Compares the horizontal position of two elements, accounting for borders
 *
 * @param {!HTMLElement} parent
 * @param {!HTMLElement} child
 * @returns {boolean}
 */
function areHorizontallyFlush(parent, child) {
  return getPageOffsetLeft(parent) + parent.clientLeft === getPageOffsetLeft(child);
}

/**
 * Compares the widths of two elements, accounting for borders and scrollbars
 *
 * @param {!HTMLElement} parent
 * @param {!HTMLElement} child
 * @returns {boolean}
 */
function widthsMatch(parent, child) {
  return parent.clientWidth === child.offsetWidth;
}
